import { ALL_FACETS, RefinementFacet } from "../instant-search/refinements";
import { ArrowLeftIcon, ArrowRightIcon } from "@vinomofo/icons";
import { Box, Button, Heading, Text } from "@vinomofo/components";
import { useEffect, useMemo, useRef, useState } from "react";

import { ClearRefinements } from "react-instantsearch-dom";
import CurrentFilterItem from "./CurrentFilterItem";
import { Media } from "gatsby-plugin-fresnel";
import { NumericMenu } from "./NumericMenu";
import { RefinementList } from "./RefinementList";
import { smoothScrollTo } from "../../utils/debounce";
import styled from "styled-components";
import { useInstantSearch } from "../../hooks/use-instantsearch";
import { useSearch } from "../../hooks/use-search";
import { FilterOnlyList } from "./FilterOnlyList";

const FilterGroupForFacet = ({
  facet: { facetType, label, attribute, urlKey, items },
}: {
  facet: RefinementFacet;
}): JSX.Element => {
  if (label && urlKey)
    switch (facetType) {
      case "RefinementFacet":
        return (
          <RefinementList label={label} attribute={attribute} asCurrentFilter />
        );
      case "MultiRangeFacet":
        return (
          <NumericMenu
            label={label}
            attribute={attribute}
            items={items}
            asCurrentFilter
          />
        );
      case "FilterOnlyFacet":
        return (
          <FilterOnlyList label={label} attribute={attribute} asCurrentFilter />
        );
      default:
        return null;
    }
  return null;
};

const CurrentFilterBox = styled(Box)<{
  withShadow?: boolean;
  shadowLeft?: boolean;
  shadowRight?: boolean;
}>`
  > * {
    display: flex;
  }

  ::-webkit-scrollbar:horizontal,
  ::-webkit-scrollbar-track,
  ::-webkit-scrollbar-thumb,
  ::-webkit-scrollbar {
    display: none;
  }

  &::before,
  &::after {
    content: "";
    opacity: 1;
    z-index: 9;
    width: 35px;
    height: 35px;
    position: absolute;
    pointer-events: none;
    transition: opacity 0.3s ease-in-out;
    top: -2px;
  }

  &::before {
    left: -1px;
    background: linear-gradient(to left, rgb(255 255 255 / 45%) 0, #fff 90%);
    display: ${({ withShadow, shadowLeft }) =>
      withShadow && shadowLeft ? "block" : "none"};
  }

  &::after {
    right: -1px;
    background: linear-gradient(to right, rgb(255 255 255 / 45%) 0, #fff 90%);
    display: ${({ withShadow, shadowRight }) =>
      withShadow && shadowRight ? "block" : "none"};
  }

  > .arrow {
    position: absolute;
    z-index: 10;
    opacity: 0.65;
    cursor: pointer;
    min-width: 35px;
    min-height: 34px;
    display: flex;
    align-items: center;
    top: -1px;

    &:hover {
      opacity: 0.9;
    }

    &.left-arrow {
      left: 0px;
      justify-content: flex-start;
      padding-left: 6px;
    }

    &.right-arrow {
      right: 0px;
      justify-content: flex-end;
      padding-right: 6px;
    }

    svg {
      width: 12px;
    }
  }

  @media ${({ theme }) => theme.mq.max.mobile} {
    &::before,
    &::after {
      top: -5px;
    }

    > .arrow {
      top: -1px;
    }
  }
`;

const ClearRefimentBox = styled(Box)`
  button {
    border: none;
    background: transparent;
    outline: none;
    cursor: pointer;
    padding: 0;

    & > * {
      color: ${({ theme }) => theme.colors.popularOffer};
      text-decoration: underline;
    }

    &:hover > * {
      color: ${({ theme }) => theme.colors.mofoGreen};
    }

    &:disabled > * {
      cursor: not-allowed;
      color: ${({ theme }) => theme.colors.disabled};
    }
  }
`;

export const ClearRefiment = (): JSX.Element => {
  return (
    <ClearRefimentBox
      pt="7px"
      pb="7px"
      ml="6px"
      alignItems="center"
      display="flex"
      justifyContent="flex-start"
    >
      <ClearRefinements
        clearsQuery
        translations={{
          reset: (
            <Box px={[15, 15, 0]} pb={[20, 20, 0]}>
              <Heading fontSize={["13px", "15px"]} as="h3">
                Clear all filters
              </Heading>
            </Box>
          ),
        }}
      />
    </ClearRefimentBox>
  );
};

export const CurrentFilter = ({
  totalHit,
}: {
  totalHit: number;
}): JSX.Element => {
  const { searchState, searching, hasResult } = useInstantSearch();
  const CurrentFilterBoxRef = useRef(null);
  const CurrentFilterInnerBoxRef = useRef(null);
  const CurrentFilterLabelRef = useRef(null);
  const CurrentFilterClearAllRef = useRef(null);
  const { updateSearch } = useSearch();
  const [isLoaded, setIsLoaded] = useState(false);
  const [shadowLeft, setShadowLeft] = useState(false);
  const [shadowRight, setShadowRight] = useState(false);
  const [withShadow, setShadow] = useState(false);
  const [fixedClearAll, setFixedClearAll] = useState(false);

  // DRAGGING
  const containerRef = CurrentFilterInnerBoxRef;
  const [isGrabbing, setIsGrabbing] = useState(false);
  const [startX, setStartX] = useState<number | null>(null);
  const [scrollLeft, setScrollLeft] = useState<number | null>(null);

  const handleMouseDown = (e: MouseEvent) => {
    if (containerRef.current) {
      setIsGrabbing(true);
      setStartX(e.clientX);
      setScrollLeft(containerRef.current.scrollLeft);
      containerRef.current.style.cursor = "grabbing";
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isGrabbing && startX !== null && scrollLeft !== null) {
      if (containerRef.current) {
        const deltaX = e.clientX - startX;
        containerRef.current.scrollLeft = scrollLeft - deltaX;
      }
    }
  };

  const handleMouseUp = () => {
    setIsGrabbing(false);
    setStartX(null);
    setScrollLeft(null);
    if (containerRef.current) {
      containerRef.current.style.cursor = "grab";
    }
  };

  const handleTouchStart = (e: TouchEvent) => {
    if (containerRef.current && e.touches.length === 1) {
      setIsGrabbing(true);
      setStartX(e.touches[0].clientX);
      setScrollLeft(containerRef.current.scrollLeft);
      containerRef.current.style.cursor = "grabbing";
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (isGrabbing && startX !== null && scrollLeft !== null) {
      if (containerRef.current && e.touches.length === 1) {
        const deltaX = e.touches[0].clientX - startX;
        containerRef.current.scrollLeft = scrollLeft - deltaX;
      }
    }
  };

  const handleTouchEnd = () => {
    setIsGrabbing(false);
    setStartX(null);
    setScrollLeft(null);
    if (containerRef.current) {
      containerRef.current.style.cursor = "grab";
    }
  };

  const handleLeftArrowClick = () => {
    if (containerRef.current) {
      // containerRef.current.scrollTo({
      //   left: containerRef.current.scrollLeft - 110,
      //   behavior: "smooth",
      // });
      smoothScrollTo(
        containerRef,
        containerRef.current?.scrollLeft - 110 || 0,
        650
      );
    }
  };

  const handleRightArrowClick = () => {
    if (containerRef.current) {
      // containerRef.current.scrollTo({
      //   left: containerRef.current.scrollLeft + 110,
      //   behavior: "smooth",
      // });
      smoothScrollTo(
        containerRef,
        containerRef.current?.scrollLeft + 110 || 0,
        650
      );
    }
  };

  const filterCount = (obj: Record<string, any>): number => {
    let totalArrayCount = 0;
    for (const key in obj) {
      if (Array.isArray(obj[key])) {
        totalArrayCount += obj[key].length;
      }
    }
    return hasResult ? totalArrayCount + 1 : totalArrayCount;
  };

  useEffect(() => {
    const element = CurrentFilterInnerBoxRef.current;
    if (!element) return;

    if (!isLoaded) {
      element.scrollLeft = 0;
      setIsLoaded(true);
    }

    const handleScroll = () => {
      const boxWidth = CurrentFilterBoxRef.current?.clientWidth + 1 || 0;
      const innerBoxWidth = CurrentFilterInnerBoxRef.current?.scrollWidth || 0;
      const labelWidth =
        (CurrentFilterLabelRef.current?.clientWidth || 0) +
        (CurrentFilterClearAllRef.current?.clientWidth || 0);

      const isOverflowing =
        boxWidth > 0 &&
        innerBoxWidth > 0 &&
        boxWidth - labelWidth <= innerBoxWidth;

      setShadow(isOverflowing);
      setFixedClearAll(isOverflowing);

      setShadowLeft(element.scrollLeft > 0);
      setShadowRight(
        element.scrollLeft < element.scrollWidth - element.clientWidth
      );
    };

    element.addEventListener("scroll", handleScroll);

    return () => {
      element.removeEventListener("scroll", handleScroll);
    };
  }, [isLoaded]);

  useEffect(() => {
    const element = CurrentFilterInnerBoxRef.current;

    if (!element) return;

    const boxWidth = CurrentFilterBoxRef.current?.clientWidth - 1 || 0;
    const innerBoxWidth = CurrentFilterInnerBoxRef.current?.scrollWidth || 0;

    const labelWidth =
      (CurrentFilterLabelRef.current?.clientWidth || 0) +
      (CurrentFilterClearAllRef.current?.clientWidth || 0);

    const isOverflowing =
      boxWidth > 0 &&
      innerBoxWidth > 0 &&
      boxWidth - labelWidth <= innerBoxWidth;

    if (isOverflowing) {
      setShadow(true);
      setFixedClearAll(true);
      setShadowLeft(true);
      setShadowRight(true);
    } else {
      setShadow(false);
      setFixedClearAll(false);
      setShadowLeft(false);
      setShadowRight(false);
    }

    if (element.scrollLeft === 0) {
      setShadowLeft(false);
    } else if (
      element.scrollLeft ===
      element.scrollWidth - element.clientWidth
    ) {
      setShadowRight(false);
    }
  }, [searchState]);

  const facetComponents = useMemo(
    () =>
      ALL_FACETS.map((facet, i) => (
        <FilterGroupForFacet key={`${facet.attribute}-${i}`} facet={facet} />
      )),
    []
  );

  return (
    <CurrentFilterBox
      ref={CurrentFilterBoxRef}
      justifyContent="flex-start"
      alignItems="flex-start"
      textAlign="center"
      overflow="hidden"
      display="flex"
      width="100%"
      position="relative"
    >
      <Media greaterThan="md">
        <Box
          pt="8px"
          pb={3}
          mb={[0, 20, 30]}
          display={["none", "none", "flex"]}
          ref={CurrentFilterLabelRef}
        >
          <Heading fontSize={["13px", "15px"]} as="h3">
            Your Filters: &nbsp;
          </Heading>
        </Box>
        <Box
          display={["none", "none", "flex"]}
          justifyContent="space-between"
          alignItems="center"
          position="absolute"
          bottom="8px"
          pl="20px"
        >
          <Text fontSize={[1]} fontWeight="500" color="darkMofo" as="small">
            Results found:&nbsp; ({totalHit})
          </Text>
        </Box>
      </Media>
      <CurrentFilterBox
        withShadow={withShadow}
        shadowLeft={shadowLeft}
        shadowRight={shadowRight}
        overflow="hidden"
        display="flex"
        width="auto"
        flex={fixedClearAll ? "1" : "unset"}
        position="relative"
      >
        {withShadow && shadowLeft && (
          <>
            <Button
              type={"button"}
              onClick={handleLeftArrowClick}
              color="mofoGreen"
              className="arrow left-arrow"
            >
              <Text color="#77B204" as="small" lineHeight={1}>
                <ArrowLeftIcon size="1.2em" />
              </Text>
            </Button>
          </>
        )}
        <CurrentFilterBox
          ref={CurrentFilterInnerBoxRef}
          justifyContent="flex-start"
          alignItems="center"
          textAlign="center"
          pb="10px"
          mb={[0, 20, 30]}
          overflow="auto"
          display="flex"
          width="auto"
          // DRAGGING
          className="scrollable-container"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {facetComponents}

          {searchState?.query &&
            searchState?.query !== "" &&
            !searching &&
            hasResult &&
            totalHit > 0 && (
              <CurrentFilterItem
                label={`"${searchState?.query}"`}
                count={totalHit}
                onClick={(event) => {
                  event.preventDefault();
                  updateSearch("");
                }}
              />
            )}
        </CurrentFilterBox>
        {withShadow && shadowRight && (
          <>
            <Button
              type={"button"}
              onClick={handleRightArrowClick}
              color="mofoGreen"
              className="arrow right-arrow"
            >
              <Text color="#77B204" as="small" lineHeight={1}>
                <ArrowRightIcon size="1.2em" />
              </Text>
            </Button>
          </>
        )}
      </CurrentFilterBox>
      <Media greaterThan="md">
        <Box pb={3} mb={[0, 20, 30]} ref={CurrentFilterClearAllRef}>
          <ClearRefiment />
        </Box>
      </Media>
    </CurrentFilterBox>
  );
};
